import type { EventAttributes, NewRelicBrowserApi } from './types';
import { postToCloudWatch } from './cloudwatch-sink';

// ─── Dev-only load-failure signal ────────────────────────────────────────────

let _agentMissingWarned = false;

function warnAgentMissingOnce(): void {
  if (process.env.NODE_ENV === 'production') return;
  if (_agentMissingWarned) return;
  _agentMissingWarned = true;
  console.warn(
    '[@workspace/newrelic-browser] window.newrelic is undefined. ' +
      'The NR Browser agent has not loaded yet. ' +
      'Check that NewRelicInitScript is in <head> and NEW_RELIC_BROWSER_ENABLED=true. ' +
      'Queued calls will be flushed once the agent loads.',
  );
}

// ─── Early-lifecycle buffering ────────────────────────────────────────────────
// The CDN loader is async — window.newrelic does not exist until the script
// downloads and executes. Calls fired during hydration / first useEffect would
// be silently dropped without this queue.

type QueuedCall = (nr: NewRelicBrowserApi) => void;
const _queue: QueuedCall[] = [];
const QUEUE_MAX = 50;
let _flushScheduled = false;

function scheduleFlush(): void {
  if (_flushScheduled || typeof window === 'undefined') return;
  _flushScheduled = true;

  // Retry loop: the CDN loader is async so window.newrelic may still be absent
  // at load time. Poll up to 20 × 250 ms = 5 s before discarding the queue.
  const tryFlush = (attempt = 0): void => {
    if (window.newrelic) {
      _queue.splice(0).forEach((fn) => {
        try {
          fn(window.newrelic!);
        } catch {
          // swallow
        }
      });
      return;
    }
    if (attempt < 20) {
      window.setTimeout(() => tryFlush(attempt + 1), 250);
    } else {
      _queue.length = 0; // agent still absent after retries — discard
      _flushScheduled = false; // allow a future call to schedule a new flush
    }
  };

  // If the page has already loaded, start the retry loop immediately.
  // Registering a 'load' listener after the event has fired would leave the
  // queue stuck forever — nothing would trigger the flush.
  if (document.readyState === 'complete') {
    tryFlush();
  } else {
    window.addEventListener('load', () => tryFlush(), { once: true });
  }
}

// ─── Core defensive wrapper ───────────────────────────────────────────────────

function withAgent(fn: (nr: NewRelicBrowserApi) => void): void {
  try {
    if (typeof window === 'undefined') return; // SSR / no window
    const nr = window.newrelic;
    if (!nr) {
      warnAgentMissingOnce();
      // Buffer if queue has space; schedule flush on window load
      if (_queue.length < QUEUE_MAX) {
        _queue.push(fn);
        scheduleFlush();
      }
      return;
    }
    fn(nr);
  } catch {
    // Never throw — fire-and-forget
  }
}

// ─── noticeError dedup ────────────────────────────────────────────────────────
// Error storms (render loops, repeated API failures) can flood NR ingest.
// Dedup by error message, capped at once per DEDUP_WINDOW_MS per session.

const DEDUP_WINDOW_MS = 5_000;
const DEDUP_MAX_KEYS = 100; // bound key-map growth
let _totalNoticeCount = 0;
const NOTICE_SESSION_MAX = 50; // per-session global ceiling
const _dedupMap = new Map<string, number>(); // key → last-fired timestamp

function shouldSendError(error: Error | string): boolean {
  if (_totalNoticeCount >= NOTICE_SESSION_MAX) return false;
  const key =
    error instanceof Error
      ? error.message.slice(0, 120)
      : String(error).slice(0, 120);
  const now = Date.now();
  const last = _dedupMap.get(key) ?? 0;
  if (now - last < DEDUP_WINDOW_MS) return false;
  // LRU-style eviction: drop oldest entry when cap is reached
  if (_dedupMap.size >= DEDUP_MAX_KEYS) {
    const oldest = _dedupMap.keys().next().value;
    if (oldest !== undefined) _dedupMap.delete(oldest);
  }
  _dedupMap.set(key, now);
  _totalNoticeCount++;
  return true;
}

// ─── Public primitives ────────────────────────────────────────────────────────

export function addPageAction(
  name: string,
  attributes?: EventAttributes,
): void {
  withAgent((nr) => nr.addPageAction(name, attributes));
  // Fan out to the CloudWatch sink when configured. Runs independently of the
  // NR agent — fires even before window.newrelic loads or if the agent is absent.
  postToCloudWatch(name, attributes as Record<string, unknown> | undefined);
}

export function setCustomAttribute(
  name: string,
  value: string | number | boolean | null,
  persist?: boolean,
): void {
  withAgent((nr) => nr.setCustomAttribute(name, value, persist));
}

/**
 * Identify the current user. Use an opaque session/customer ID — NEVER a
 * hashed national ID (CNP/PESEL). Call with null on logout so shared-device
 * sessions do not carry over a previous user's identity.
 */
export function setUserId(value: string | null): void {
  withAgent((nr) => nr.setUserId(value));
}

/**
 * Record a handled error. Built-in dedup: same error signature fires at most
 * once per DEDUP_WINDOW_MS per session (prevents error-storm ingest flooding).
 */
export function noticeError(
  error: Error | string,
  customAttributes?: EventAttributes,
): void {
  if (!shouldSendError(error)) return;
  withAgent((nr) => nr.noticeError(error, customAttributes));
}

/**
 * Set a readable name for the current page/route.
 * Must be called alongside setCurrentRouteName on SPA route changes for full
 * coverage of both PageView and BrowserInteraction events.
 * Use route TEMPLATES not resolved paths: '/loans/[id]' not '/loans/123'.
 */
export function setPageViewName(name: string, host?: string): void {
  withAgent((nr) => nr.setPageViewName(name, host));
}

/**
 * Set a readable name for the current SPA BrowserInteraction event.
 * Must be called on every route change (setPageViewName alone is not enough).
 * Requires Pro+SPA agent. Use route templates, not resolved paths.
 */
export function setCurrentRouteName(name: string | null): void {
  withAgent((nr) => nr.setCurrentRouteName(name));
}

/** Expose reset helper for testing. */
export function _resetForTesting(): void {
  _agentMissingWarned = false;
  _queue.length = 0;
  _flushScheduled = false;
  _dedupMap.clear();
  _totalNoticeCount = 0;
}
